import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, Scatter, ScatterChart, ZAxis, Treemap, TreemapItem,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { 
  TrendingUp, TrendingDown, BarChart3, PieChart as PieChartIcon,
  Activity, Target, Zap, DollarSign, Clock, Users, Package,
  ArrowUpRight, ArrowDownRight, RefreshCw, Filter, Search,
  Calendar, Layers, Cube, Database, Globe, MapPin
} from 'lucide-react';

const AnalyticsTab = ({ timeRange }) => {
  const [selectedMetric, setSelectedMetric] = useState('efficiency');
  const [predictionPeriod, setPredictionPeriod] = useState('30d');

  // Mock data for analytics
  const performanceData = [
    { month: 'Jan', efficiency: 85, cost: 45000, delivery: 92, satisfaction: 88, utilization: 78 },
    { month: 'Feb', efficiency: 87, cost: 48000, delivery: 94, satisfaction: 90, utilization: 82 },
    { month: 'Mar', efficiency: 82, cost: 42000, delivery: 89, satisfaction: 85, utilization: 75 },
    { month: 'Apr', efficiency: 90, cost: 52000, delivery: 96, satisfaction: 92, utilization: 88 },
    { month: 'May', efficiency: 88, cost: 47000, delivery: 93, satisfaction: 89, utilization: 85 },
    { month: 'Jun', efficiency: 92, cost: 58000, delivery: 97, satisfaction: 94, utilization: 90 },
    { month: 'Jul', efficiency: 94, cost: 62000, delivery: 98, satisfaction: 96, utilization: 92 },
    { month: 'Aug', efficiency: 96, cost: 65000, delivery: 99, satisfaction: 97, utilization: 94 }
  ];

  const predictiveData = [
    { month: 'Sep', predicted: 97, actual: null, confidence: 0.92 },
    { month: 'Oct', predicted: 98, actual: null, confidence: 0.89 },
    { month: 'Nov', predicted: 99, actual: null, confidence: 0.85 },
    { month: 'Dec', predicted: 100, actual: null, confidence: 0.82 }
  ];

  const heatmapData = [
    { day: 'Mon', hour: '00', value: 25 }, { day: 'Mon', hour: '06', value: 45 },
    { day: 'Mon', hour: '12', value: 85 }, { day: 'Mon', hour: '18', value: 65 },
    { day: 'Tue', hour: '00', value: 30 }, { day: 'Tue', hour: '06', value: 50 },
    { day: 'Tue', hour: '12', value: 90 }, { day: 'Tue', hour: '18', value: 70 },
    { day: 'Wed', hour: '00', value: 35 }, { day: 'Wed', hour: '06', value: 55 },
    { day: 'Wed', hour: '12', value: 95 }, { day: 'Wed', hour: '18', value: 75 },
    { day: 'Thu', hour: '00', value: 40 }, { day: 'Thu', hour: '06', value: 60 },
    { day: 'Thu', hour: '12', value: 100 }, { day: 'Thu', hour: '18', value: 80 },
    { day: 'Fri', hour: '00', value: 45 }, { day: 'Fri', hour: '06', value: 65 },
    { day: 'Fri', hour: '12', value: 105 }, { day: 'Fri', hour: '18', value: 85 },
    { day: 'Sat', hour: '00', value: 50 }, { day: 'Sat', hour: '06', value: 70 },
    { day: 'Sat', hour: '12', value: 110 }, { day: 'Sat', hour: '18', value: 90 },
    { day: 'Sun', hour: '00', value: 55 }, { day: 'Sun', hour: '06', value: 75 },
    { day: 'Sun', hour: '12', value: 115 }, { day: 'Sun', hour: '18', value: 95 }
  ];

  const kpiData = [
    { name: 'Efficiency', value: 92, target: 95, trend: 'up', change: '+3.2%' },
    { name: 'Cost Optimization', value: 87, target: 90, trend: 'up', change: '+2.8%' },
    { name: 'Delivery Speed', value: 94, target: 96, trend: 'up', change: '+1.5%' },
    { name: 'Customer Satisfaction', value: 89, target: 92, trend: 'up', change: '+2.1%' },
    { name: 'Resource Utilization', value: 85, target: 88, trend: 'down', change: '-1.2%' },
    { name: 'Inventory Turnover', value: 91, target: 94, trend: 'up', change: '+3.7%' }
  ];

  const regionalPerformance = [
    { region: 'Central', efficiency: 94, cost: 52000, delivery: 96, satisfaction: 92 },
    { region: 'Western', efficiency: 89, cost: 48000, delivery: 93, satisfaction: 88 },
    { region: 'Eastern', efficiency: 91, cost: 50000, delivery: 95, satisfaction: 90 },
    { region: 'Southern', efficiency: 87, cost: 46000, delivery: 91, satisfaction: 86 },
    { region: 'Northern', efficiency: 93, cost: 51000, delivery: 94, satisfaction: 91 }
  ];

  const getHeatmapColor = (value) => {
    if (value >= 100) return 'bg-red-500';
    if (value >= 80) return 'bg-orange-500';
    if (value >= 60) return 'bg-yellow-500';
    if (value >= 40) return 'bg-blue-500';
    return 'bg-gray-300';
  };

  const getTrendIcon = (trend) => {
    return trend === 'up' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />;
  };

  const getTrendColor = (trend) => {
    return trend === 'up' ? 'text-green-600' : 'text-red-600';
  };

  return (
    <div className="p-8 space-y-8">
      {/* Analytics Header */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="bg-gradient-to-r from-orange-600 to-red-600 rounded-3xl p-6 text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Advanced Analytics</h2>
            <p className="text-orange-100">Predictive insights and performance optimization</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm">AI Powered</span>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300"
            >
              <RefreshCw className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* KPI Dashboard */}
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {kpiData.map((kpi, index) => (
          <motion.div
            key={kpi.name}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
            className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20 hover:bg-white/80 transition-all duration-300"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <div className={`p-2 rounded-xl ${getTrendColor(kpi.trend)} bg-opacity-10`}>
                  {getTrendIcon(kpi.trend)}
                </div>
                <span className="text-sm font-medium text-gray-600">{kpi.name}</span>
              </div>
              <div className={`flex items-center text-sm ${getTrendColor(kpi.trend)}`}>
                {kpi.change}
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-gray-900">{kpi.value}%</span>
                <span className="text-sm text-gray-500">Target: {kpi.target}%</span>
              </div>
              
              <div className="relative">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <motion.div
                    className={`h-2 rounded-full ${
                      kpi.value >= kpi.target ? 'bg-green-500' : 'bg-orange-500'
                    }`}
                    initial={{ width: 0 }}
                    animate={{ width: `${(kpi.value / kpi.target) * 100}%` }}
                    transition={{ duration: 1, delay: index * 0.1 }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Performance Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Performance Trends */}
        <motion.div
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Performance Trends</h3>
              <p className="text-gray-600">Historical and predictive data</p>
            </div>
            <div className="flex items-center space-x-2">
              <select
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
                className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="efficiency">Efficiency</option>
                <option value="cost">Cost</option>
                <option value="delivery">Delivery</option>
                <option value="satisfaction">Satisfaction</option>
              </select>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={[...performanceData, ...predictiveData]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                  border: 'none', 
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey={selectedMetric} 
                stroke="#F59E0B" 
                strokeWidth={3}
                dot={{ fill: '#F59E0B', strokeWidth: 2, r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="predicted" 
                stroke="#EF4444" 
                strokeWidth={2}
                strokeDasharray="5,5"
                dot={{ fill: '#EF4444', strokeWidth: 2, r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Regional Performance */}
        <motion.div
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Regional Performance</h3>
              <p className="text-gray-600">Performance by region</p>
            </div>
            <Globe className="w-6 h-6 text-orange-600" />
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={regionalPerformance}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis dataKey="region" stroke="#6b7280" />
              <PolarRadiusAxis stroke="#6b7280" />
              <Radar
                name="Efficiency"
                dataKey="efficiency"
                stroke="#F59E0B"
                fill="#F59E0B"
                fillOpacity={0.3}
              />
              <Radar
                name="Delivery"
                dataKey="delivery"
                stroke="#EF4444"
                fill="#EF4444"
                fillOpacity={0.3}
              />
              <Radar
                name="Satisfaction"
                dataKey="satisfaction"
                stroke="#10B981"
                fill="#10B981"
                fillOpacity={0.3}
              />
              <Legend />
            </RadarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Activity Heatmap */}
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Activity Heatmap</h3>
            <p className="text-gray-600">Weekly activity patterns</p>
          </div>
          <Activity className="w-6 h-6 text-orange-600" />
        </div>

        <div className="grid grid-cols-7 gap-2">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
            <div key={day} className="text-center text-sm font-medium text-gray-600 mb-2">
              {day}
            </div>
          ))}
          
          {['00', '06', '12', '18'].map(hour => (
            <div key={hour} className="text-right text-sm text-gray-600 pr-2 flex items-center justify-end">
              {hour}:00
            </div>
          ))}
          
          {heatmapData.map((item, index) => (
            <motion.div
              key={`${item.day}-${item.hour}`}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5 + index * 0.01, duration: 0.3 }}
              className={`w-8 h-8 rounded ${getHeatmapColor(item.value)} hover:scale-110 transition-transform duration-200 cursor-pointer`}
              title={`${item.day} ${item.hour}:00 - Activity Level: ${item.value}`}
            />
          ))}
        </div>

        <div className="flex items-center justify-center space-x-4 mt-6">
          <span className="text-sm text-gray-600">Low</span>
          <div className="flex space-x-1">
            <div className="w-4 h-4 bg-gray-300 rounded"></div>
            <div className="w-4 h-4 bg-blue-500 rounded"></div>
            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
            <div className="w-4 h-4 bg-orange-500 rounded"></div>
            <div className="w-4 h-4 bg-red-500 rounded"></div>
          </div>
          <span className="text-sm text-gray-600">High</span>
        </div>
      </motion.div>

      {/* Predictive Insights */}
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="grid grid-cols-1 lg:grid-cols-3 gap-6"
      >
        {[
          {
            title: 'Demand Forecast',
            value: '+15.2%',
            description: 'Expected increase in next quarter',
            icon: TrendingUp,
            color: 'from-green-500 to-emerald-500'
          },
          {
            title: 'Cost Optimization',
            value: '-8.5%',
            description: 'Potential savings identified',
            icon: DollarSign,
            color: 'from-blue-500 to-cyan-500'
          },
          {
            title: 'Efficiency Boost',
            value: '+12.3%',
            description: 'AI-driven improvements',
            icon: Zap,
            color: 'from-purple-500 to-pink-500'
          }
        ].map((insight, index) => (
          <motion.div
            key={insight.title}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.6 + index * 0.1, duration: 0.5 }}
            className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20 hover:bg-white/80 transition-all duration-300"
          >
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${insight.color} flex items-center justify-center mb-4`}>
              <insight.icon className="w-6 h-6 text-white" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">{insight.title}</h4>
            <div className="text-2xl font-bold text-gray-900 mb-2">{insight.value}</div>
            <p className="text-gray-600 text-sm">{insight.description}</p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export default AnalyticsTab; 